import { useCallback } from 'react';
import { DATA_ADAPTER } from '../config/adapters';

export interface RequestLiveChatParams {
  houseId: string;
  summary?: string;
}

export interface LiveChatSession {
  id: string;
  status: 'queued' | 'active' | 'closed' | 'unavailable';
  agentId?: string | null;
}

async function callLive(name: string, data: Record<string, unknown> | object = {}) {
  if (DATA_ADAPTER === 'mock') {
    if (name === 'requestLiveSupport') {
      return { id: `live_mock_${Date.now()}`, status: 'unavailable' as const };
    }
    return { ok: true };
  }
  const { callFunction } = await import('../services/firebase/FunctionsService');
  return callFunction(name, data);
}

export function useLiveSupport() {
  const requestLiveChat = useCallback(async (params: RequestLiveChatParams): Promise<LiveChatSession> => {
    const res = await callLive('requestLiveSupport', params) as LiveChatSession;
    return res;
  }, []);

  const heartbeatPresence = useCallback(async (houseIds: string[]) => {
    await callLive('heartbeatSupportPresence', { houseIds });
  }, []);

  const claimSession = useCallback(async (sessionId: string, houseId: string) => {
    return callLive('claimLiveSupport', { sessionId, houseId });
  }, []);

  const sendLiveMessage = useCallback(async (opts: {
    sessionId: string;
    houseId: string;
    text: string;
  }) => callLive('sendLiveSupportMessage', opts), []);

  const closeLiveSession = useCallback(async (opts: {
    sessionId: string;
    houseId: string;
    convertToTicket?: boolean;
  }) => callLive('closeLiveSupport', opts), []);

  return {
    requestLiveChat,
    heartbeatPresence,
    claimSession,
    sendLiveMessage,
    closeLiveSession,
  };
}
